import * as React from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import WaterDropRoundedIcon from "@mui/icons-material/WaterDropRounded";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import LocalHospitalRoundedIcon from "@mui/icons-material/LocalHospitalRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import { useNavigate } from "react-router-dom";

import { Colors } from "../../utils/Colors";
import { ROUTES } from "../../shared/constants/routes";

const http = axios.create({
  baseURL: "http://127.0.0.1:8000",
  timeout: 20000,
});

const API = {
  fluidSummary: "/api/medcharts/fluid-therapy-patient-summary/?format=json",
  medicationSummary: "/api/medcharts/medication-patient-summary/?format=json",
  investigationSummary: "/api/investigations/patient-summary/",
};

function toArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function fmt(value) {
  if (value === undefined || value === null || value === "") return "—";
  return String(value);
}

function normalizeKey(value) {
  return String(value ?? "").trim().toLowerCase();
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function getLatestDate(...values) {
  const valid = values
    .filter(Boolean)
    .map((v) => new Date(v))
    .filter((d) => !Number.isNaN(d.getTime()));

  if (!valid.length) return "";

  valid.sort((a, b) => b.getTime() - a.getTime());
  return valid[0].toISOString();
}

function buildMergedPatientMap(fluidPatients, medicationPatients, investigationPatients) {
  const map = new Map();

  function ensurePatient(base) {
    const key = normalizeKey(base.patient_uhid);
    if (!key) return null;

    if (!map.has(key)) {
      map.set(key, {
        patient_uhid: base.patient_uhid || "",
        patient_name: base.patient_name || "",
        admission_number: base.admission_number || "",
        doctor_name: base.doctor_name || "",
        total_fluid_times: 0,
        total_medications: 0,
        total_investigations: 0,
        last_fluid_at: "",
        last_medication_at: "",
        last_investigation_at: "",
      });
    }

    const row = map.get(key);

    if (!row.patient_name && base.patient_name) row.patient_name = base.patient_name;
    if (!row.admission_number && base.admission_number) row.admission_number = base.admission_number;
    if (!row.doctor_name && base.doctor_name) row.doctor_name = base.doctor_name;

    return row;
  }

  fluidPatients.forEach((row) => {
    const patient = ensurePatient(row);
    if (!patient) return;
    patient.total_fluid_times = Number(row.total_fluid_times || 0);
    patient.last_fluid_at = row.last_fluid_at || "";
  });

  medicationPatients.forEach((row) => {
    const patient = ensurePatient(row);
    if (!patient) return;
    patient.total_medications = Number(row.total_medications || 0);
    patient.last_medication_at = row.last_medication_at || "";
  });

  investigationPatients.forEach((row) => {
    const patient = ensurePatient(row);
    if (!patient) return;
    patient.total_investigations = Number(row.total_investigations || 0);
    patient.last_investigation_at = row.last_investigation_at || "";
  });

  return Array.from(map.values())
    .map((row, index) => ({
      id: `${row.patient_uhid}-${index}`,
      ...row,
      last_activity_at: getLatestDate(
        row.last_fluid_at,
        row.last_medication_at,
        row.last_investigation_at
      ),
    }))
    .sort((a, b) => {
      const aTime = a.last_activity_at ? new Date(a.last_activity_at).getTime() : 0;
      const bTime = b.last_activity_at ? new Date(b.last_activity_at).getTime() : 0;
      return bTime - aTime;
    });
}

function DashboardStatCard({ title, value, icon, color, bg }) {
  return (
    <Card
      sx={{
        borderRadius: 2.5,
        border: `1px solid ${Colors.border.light}`,
        background: bg,
        boxShadow: "0 6px 18px rgba(11,60,63,0.08)",
        height: "100%",
      }}
    >
      <CardContent sx={{ p: 1.4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 800,
                color: Colors.text.muted,
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>

            <Typography
              sx={{
                mt: 0.6,
                fontSize: { xs: 24, md: 30 },
                fontWeight: 900,
                color: Colors.text.title,
                lineHeight: 1,
              }}
            >
              {fmt(value)}
            </Typography>
          </Box>

          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "12px",
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(color, 0.14),
              color,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function SmallInfoCard({ icon, label, value }) {
  return (
    <Box
      sx={{
        p: 1.05,
        borderRadius: 2,
        border: `1px solid ${Colors.border.light}`,
        bgcolor: Colors.bg.hover,
        minHeight: 72,
      }}
    >
      <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.6 }}>
        <Box sx={{ color: Colors.text.icon, display: "grid", placeItems: "center" }}>
          {icon}
        </Box>
        <Typography
          sx={{
            fontSize: 10.5,
            color: Colors.text.muted,
            fontWeight: 800,
            lineHeight: 1.2,
          }}
        >
          {label}
        </Typography>
      </Stack>

      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 900,
          color: Colors.text.body,
          lineHeight: 1.25,
          wordBreak: "break-word",
        }}
      >
        {fmt(value)}
      </Typography>
    </Box>
  );
}

function MetricMiniCard({ label, value, color, icon }) {
  return (
    <Box
      sx={{
        p: 0.95,
        borderRadius: 2,
        border: `1px solid ${Colors.border.light}`,
        bgcolor: alpha(color, 0.08),
        textAlign: "center",
        height: "100%",
      }}
    >
      <Stack spacing={0.35} alignItems="center">
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "8px",
            display: "grid",
            placeItems: "center",
            color,
            bgcolor: alpha(color, 0.12),
          }}
        >
          {icon}
        </Box>

        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 900,
            color: Colors.text.title,
            lineHeight: 1,
          }}
        >
          {fmt(value)}
        </Typography>

        <Typography
          sx={{
            fontSize: 10.5,
            fontWeight: 800,
            color: Colors.text.body,
            lineHeight: 1.15,
          }}
        >
          {label}
        </Typography>
      </Stack>
    </Box>
  );
}

function PatientSummaryCard({ row, onViewHistory }) {
  return (
    <Card
      sx={{
        borderRadius: 3,
        border: `1px solid ${Colors.border.light}`,
        background: Colors.bg.paper,
        boxShadow: "0 10px 24px rgba(11,60,63,0.10)",
        height: "100%",
        transition: "transform 0.18s ease, box-shadow 0.18s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 14px 28px rgba(11,60,63,0.14)",
        },
      }}
    >
      <CardContent sx={{ p: 1.5 }}>
        <Stack spacing={1.2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 900,
                  color: Colors.text.title,
                  wordBreak: "break-word",
                  lineHeight: 1.2,
                }}
              >
                {fmt(row.patient_name)}
              </Typography>

              <Typography
                sx={{
                  mt: 0.35,
                  fontSize: 12,
                  color: Colors.text.muted,
                  fontWeight: 700,
                }}
              >
                UHID: {fmt(row.patient_uhid)}
              </Typography>
            </Box>

            <Chip
              icon={<PersonRoundedIcon sx={{ fontSize: "16px !important" }} />}
              label="Patient"
              size="small"
              sx={{
                height: 28,
                bgcolor: alpha(Colors.brand.primary, 0.1),
                color: Colors.text.body,
                fontWeight: 800,
                "& .MuiChip-label": { px: 1, fontSize: 11 },
              }}
            />
          </Stack>

          <Grid container spacing={1}>
            <Grid item xs={6}>
              <SmallInfoCard
                icon={<BadgeRoundedIcon fontSize="small" />}
                label="Admission No"
                value={row.admission_number}
              />
            </Grid>
            <Grid item xs={6}>
              <SmallInfoCard
                icon={<LocalHospitalRoundedIcon fontSize="small" />}
                label="Doctor Name"
                value={row.doctor_name}
              />
            </Grid>
          </Grid>

          <Grid container spacing={1}>
            <Grid item xs={4}>
              <MetricMiniCard
                label="Fluid"
                value={row.total_fluid_times || 0}
                color={Colors.status.success}
                icon={<WaterDropRoundedIcon sx={{ fontSize: 14 }} />}
              />
            </Grid>

            <Grid item xs={4}>
              <MetricMiniCard
                label="Medicine"
                value={row.total_medications || 0}
                color={Colors.status.info}
                icon={<MedicationOutlinedIcon sx={{ fontSize: 14 }} />}
              />
            </Grid>

            <Grid item xs={4}>
              <MetricMiniCard
                label="Investigation"
                value={row.total_investigations || 0}
                color={Colors.status.warning}
                icon={<ScienceRoundedIcon sx={{ fontSize: 14 }} />}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              p: 1.05,
              borderRadius: 2,
              border: `1px solid ${Colors.border.light}`,
              bgcolor: Colors.bg.hover,
            }}
          >
            <Stack direction="row" spacing={0.8} alignItems="center">
              <AccessTimeRoundedIcon sx={{ fontSize: 15, color: Colors.text.icon }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: Colors.text.muted }}>
                  Last Activity
                </Typography>
                <Typography
                  sx={{
                    mt: 0.2,
                    fontSize: 12.5,
                    fontWeight: 900,
                    color: Colors.text.body,
                    lineHeight: 1.25,
                  }}
                >
                  {formatDateTime(row.last_activity_at)}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Button
            fullWidth
            variant="contained"
            startIcon={<VisibilityRoundedIcon />}
            onClick={() => onViewHistory(row)}
            sx={{
              borderRadius: 2.2,
              textTransform: "none",
              fontWeight: 900,
              py: 1,
              background: Colors.brand.primary,
              boxShadow: "none",
              "&:hover": {
                background: Colors.brand.primaryDark,
                boxShadow: "none",
              },
            }}
          >
            View History
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function PatientClinicalSummaryPage() {
  const navigate = useNavigate();

  const [searchText, setSearchText] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const fetchSummary = React.useCallback(async (search = "") => {
    try {
      setLoading(true);
      setError("");

      const fluidUrl = search
        ? `${API.fluidSummary}&search=${encodeURIComponent(search)}`
        : API.fluidSummary;

      const medicationUrl = search
        ? `${API.medicationSummary}&search=${encodeURIComponent(search)}`
        : API.medicationSummary;

      const investigationUrl = search
        ? `${API.investigationSummary}?search=${encodeURIComponent(search)}`
        : API.investigationSummary;

      const [fluidRes, medicationRes, investigationRes] = await Promise.all([
        http.get(fluidUrl),
        http.get(medicationUrl),
        http.get(investigationUrl),
      ]);

      const merged = buildMergedPatientMap(
        toArray(fluidRes.data),
        toArray(medicationRes.data),
        toArray(investigationRes.data)
      );

      setRows(merged);
    } catch (err) {
      console.error("Summary load error:", err);
      setError("Unable to load patient summary.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchSummary("");
  }, [fetchSummary]);

  const handleSearch = () => {
    fetchSummary(searchText.trim());
  };

  const handleReset = () => {
    setSearchText("");
    fetchSummary("");
  };

  const handleViewHistory = (patient) => {
    navigate(ROUTES.clinical_overview_details(patient.patient_uhid), {
      state: { patient },
    });
  };

  const totalPatients = rows.length;
  const totalFluid = rows.reduce((sum, row) => sum + Number(row.total_fluid_times || 0), 0);
  const totalMedication = rows.reduce((sum, row) => sum + Number(row.total_medications || 0), 0);
  const totalInvestigation = rows.reduce((sum, row) => sum + Number(row.total_investigations || 0), 0);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: Colors.bg.app,
        p: { xs: 1, md: 1.6 },
      }}
    >
      <Stack spacing={1.4}>
        <Card
          sx={{
            borderRadius: 3,
            border: `1px solid ${Colors.border.light}`,
            background: Colors.bg.paper,
            boxShadow: "0 10px 26px rgba(11,60,63,0.10)",
          }}
        >
          <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} lg={6.5}>
                <Box>
                  <Typography
                    sx={{
                      fontSize: { xs: 22, md: 26 },
                      fontWeight: 900,
                      color: Colors.text.title,
                      lineHeight: 1.1,
                      letterSpacing: 0.2,
                    }}
                  >
                    PATIENT CLINICAL SUMMARY
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.5,
                      fontSize: 12.5,
                      color: Colors.text.muted,
                      lineHeight: 1.5,
                    }}
                  >
                    View patient-wise summary of fluid therapy, medication, and investigation records.
                    Open full details by clicking view history.
                  </Typography>

                  <Box
                    sx={{
                      mt: 1.2,
                      height: 5,
                      width: 160,
                      borderRadius: 999,
                      background: `linear-gradient(90deg, ${Colors.brand.primary}, ${alpha(
                        Colors.brand.primary,
                        0.18
                      )})`,
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} lg={5.5}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  justifyContent="flex-end"
                >
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search patient by name or UHID"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSearch();
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: Colors.bg.paper,
                        height: 42,
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon sx={{ color: Colors.text.icon, fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    sx={{
                      minWidth: 104,
                      height: 42,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 900,
                      background: Colors.brand.primary,
                      "&:hover": {
                        background: Colors.brand.primaryDark,
                      },
                    }}
                  >
                    Search
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    startIcon={<RestartAltRoundedIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      minWidth: 92,
                      height: 42,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 800,
                      color: Colors.text.body,
                      borderColor: Colors.border.input,
                    }}
                  >
                    Reset
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Grid container spacing={1.2}>
          <Grid item xs={12} sm={6} md={3}>
            <DashboardStatCard
              title="Total Patients"
              value={totalPatients}
              icon={<PeopleAltRoundedIcon sx={{ fontSize: 18 }} />}
              color={Colors.brand.primary}
              bg={alpha(Colors.brand.primary, 0.05)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <DashboardStatCard
              title="Fluid Records"
              value={totalFluid}
              icon={<WaterDropRoundedIcon sx={{ fontSize: 18 }} />}
              color={Colors.status.success}
              bg={alpha(Colors.status.success, 0.08)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <DashboardStatCard
              title="Medication Records"
              value={totalMedication}
              icon={<MedicationOutlinedIcon sx={{ fontSize: 18 }} />}
              color={Colors.status.info}
              bg={alpha(Colors.status.info, 0.08)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <DashboardStatCard
              title="Investigation Records"
              value={totalInvestigation}
              icon={<ScienceRoundedIcon sx={{ fontSize: 18 }} />}
              color={Colors.status.warning}
              bg={alpha(Colors.status.warning, 0.12)}
            />
          </Grid>
        </Grid>

        {error ? (
          <Alert severity="error" sx={{ borderRadius: 2.5 }}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Card
            sx={{
              borderRadius: 3,
              border: `1px solid ${Colors.border.light}`,
              boxShadow: "0 10px 26px rgba(11,60,63,0.10)",
            }}
          >
            <CardContent sx={{ py: 6, textAlign: "center" }}>
              <CircularProgress sx={{ color: Colors.brand.primary }} />
              <Typography sx={{ mt: 1.5, color: Colors.text.title, fontWeight: 900 }}>
                Loading patient summary...
              </Typography>
            </CardContent>
          </Card>
        ) : rows.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2.5 }}>
            No patient summary found.
          </Alert>
        ) : (
          <Grid container spacing={1.4}>
            {rows.map((row) => (
              <Grid item xs={12} sm={6} lg={4} xl={3} key={row.id}>
                <PatientSummaryCard row={row} onViewHistory={handleViewHistory} />
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>
    </Box>
  );
}